import { useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import ButtonWithLoading from "components/ButtonWithLoading";
import ApplicationStatus, { SyncStatus } from "../ApplicationStatus";
import { StorageSettingsUpdate } from "../../types";
import useMailboxAuthPopup from "utils/useMailboxAuthPopup";
import { Button } from "@material-ui/core";
import VerifyAccountModal from "./VerifyAccountModal";

interface Props {
  settings: StorageSettingsUpdate;
  onAccountSync: (update: StorageSettingsUpdate) => void;
}

export default function OfficeMailboxSync({ settings, onAccountSync }: Props) {
  const { t } = useTranslation("storage");
  const { enqueueSnackbar } = useSnackbar();
  const { open, isClosedByUser, isAccessDenied } = useMailboxAuthPopup();
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { office365 } = settings;

  const status = getStatus(office365);

  const onAddOrgMailboxAccount = async () => {
    setLoading(true);
    try {
      const res = await open("office365/organization");
      if (!res.auth) return;

      onAccountSync({
        ...settings,
        office365: { ...settings.office365, mailbox: true },
      });
    } catch (error) {
      if (isClosedByUser(error)) return;

      enqueueSnackbar(t("officeMailboxSync.addError"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const reInstall = async () => {
    try {
      const res = await open("office365/organization");
      if (res.auth) {
        enqueueSnackbar(t("officeMailboxSync.reinstall.ok"), {
          variant: "success",
        });
        onAccountSync({
          ...settings,
          office365: {
            ...settings.office365,
            mailbox: true,
            mailboxReconsentRequired: false,
          },
        });
      }
    } catch (error) {
      if (isClosedByUser(error)) return;

      if (isAccessDenied(error)) {
        enqueueSnackbar(t("officeMailboxSync.reinstall.rejected"));
        return;
      }

      enqueueSnackbar(t("officeMailboxSync.addError"), { variant: "error" });
    }
  };

  const onVerify = () => setIsModalOpen(true);

  return (
    <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
      <ApplicationStatus status={status} />
      {status === SyncStatus.NOT_INSTALLED && (
        <ButtonWithLoading
          variant="text"
          color="primary"
          loading={loading}
          onClick={onAddOrgMailboxAccount}
        >
          {t("install")}
        </ButtonWithLoading>
      )}

      {status !== SyncStatus.NOT_INSTALLED && (
        <>
          <Button variant="text" color="primary" onClick={reInstall}>
            {t("reInstall")}
          </Button>
          <Button variant="text" color="primary" onClick={onVerify}>
            {t("verify")}
          </Button>
        </>
      )}
      {isModalOpen && (
        <VerifyAccountModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
}

// El admin del tenant tiene que volver a dar consent al app de organizacion
// para los permisos de Graph (MXH-5916): mismo boton "Re-install". `null` es
// que la API no pudo preguntarle a Microsoft.
function getStatus(office365: StorageSettingsUpdate["office365"]) {
  if (!office365.mailbox) return SyncStatus.NOT_INSTALLED;
  if (office365.mailboxReconsentRequired === null)
    return SyncStatus.NOT_VERIFIED;
  if (office365.mailboxReconsentRequired) return SyncStatus.RECONSENT_REQUIRED;
  return SyncStatus.INSTALLED;
}
